import { Trans } from '@lingui/macro'

import * as React from 'react'

import { generateRandomNumberArray } from '~logic/helpers/arrayUtils/generateRandomNumberArray'
import { TProductDetailState } from '~logic/xstate/machines/menu/productDetailMachineTypes'
import { menuScreenElementIds } from '~shared/config/menuScreenElementIds'
import { useProductDetailDataCtx, useProductDetailStateCtx } from '~shared/segments/ProductDetail'
import { Box, Grid } from '~ui/components/layout/Box'
import { Hr } from '~ui/components/layout/Hr'
import { H3 } from '~ui/components/typography/Heading/Heading'
import { TSingleSpace } from '~ui/styleGuide/spaces'

import { useLayoutCtx } from '../../../config/contexts/LayoutProvider'
import { getAddonListPlaceholders } from '../helpers/getAddonListPlaceholders'
import { AddonCategoryNavigation } from './AddonCategoryNavigation'
import { AddonListCategory } from './AddonListCategory'

type TAddonCategory = TProductDetailState['addonCategories'][number]

type TProps = NoChildren & {
  /**
   * height of the sticky content = addon category navigation
   */
  stuckContentHeight: PropsOf<typeof AddonCategoryNavigation>['stuckContentHeight']
}

const { addonListContainerId } = menuScreenElementIds
const categoryGap: TSingleSpace = `large`

/**
 * Renders addon categories in the logical order.
 * *mandatory categories first*
 */
export const AddonList: React.FC<TProps> = ({ stuckContentHeight }) => {
  const { product } = useProductDetailDataCtx()
  const { addonCategories } = useProductDetailStateCtx()
  const { isMobile } = useLayoutCtx()

  if (product.availableAddonCount === 0) {
    return null
  }

  const showSkeleton = !addonCategories

  const renderAddonCategory = (category: TAddonCategory) => (
    <AddonListCategory key={category.id} category={category} showSkeleton={showSkeleton} />
  )

  if (showSkeleton) {
    // create placeholder categories with addons mapped to the shape of the real data
    const placeholderItemCounts = generateRandomNumberArray({ length: product.availableAddonCount })
    const placeholderCategories = getAddonListPlaceholders(placeholderItemCounts)

    return <Grid rowGap={categoryGap}>{placeholderCategories.map(renderAddonCategory)}</Grid>
  }

  const mandatoryAddonCategories = addonCategories.filter((category) => isMandatory(category))
  const optionalAddonCategories = addonCategories.filter((category) => !isMandatory(category))

  const hasOptionalAddons = optionalAddonCategories.length > 0
  const hasMandatoryAddons = mandatoryAddonCategories.length > 0
  const hasMoreOptionalCategories = optionalAddonCategories.length > 1

  return (
    <Grid rowGap={categoryGap} id={addonListContainerId}>
      {hasMandatoryAddons && (
        <Grid
          rowGap={categoryGap}
          padding={{
            top: isMobile ? `medium` : `none`,
            bottom: `none`,
            right: `none`,
            left: `none`,
          }}
        >
          {mandatoryAddonCategories.map(renderAddonCategory)}
        </Grid>
      )}

      {hasOptionalAddons && (
        <Box
          padding={{
            top: `medium`,
            bottom: `none`,
            right: isMobile ? `medium` : `none`,
            left: isMobile ? `medium` : `none`,
          }}
        >
          <H3 fontStyle={`regular`} fontSize={`m16`}>
            {hasMandatoryAddons ? <Trans>Additional addons</Trans> : <Trans>Addons</Trans>}
          </H3>
          <Hr />
        </Box>
      )}

      {isMobile && hasMoreOptionalCategories && (
        <AddonCategoryNavigation
          stuckContentHeight={stuckContentHeight}
          addonCategories={optionalAddonCategories}
        />
      )}

      {optionalAddonCategories.map(renderAddonCategory)}
    </Grid>
  )
}

// returns true when customer has to pick at least one addon from the category
const isMandatory = (addonCategory: TProductDetailState['addonCategories'][number]) => {
  return Boolean(addonCategory.minTotalCount > 0)
}
